<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ pageTitle() || 'KYC Review' }}</h2>
            <p>Review partner KYC submissions, open documents, then approve or reject.</p>
        </div>
    </div>

    <div class="card">
        <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
                <p-inputicon styleClass="pi pi-search" />
                <input type="text" pInputText placeholder="Search partner, code, name or PAN..."
                    [ngModel]="search()" (ngModelChange)="search.set($event)" />
            </p-iconfield>
            <div class="status-tabs">
                @for (f of statusFilters; track f) {
                <button type="button" [class.on]="statusFilter() === f" (click)="onFilter(f)">{{ f }}</button>
                }
            </div>
            <span class="count">{{ filtered().length }} of {{ rows().length }}</span>
        </div>

        <p-table [value]="filtered()" [loading]="loading()" dataKey="id" [rows]="10"
            [paginator]="filtered().length > 10" [rowsPerPageOptions]="[10, 25, 50]"
            styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
            <ng-template pTemplate="header">
                <tr>
                    <th>Partner</th>
                    <th>Company Name</th>
                    <th>PAN</th>
                    <th>Registration</th>
                    <th>GST</th>
                    <th>Bank</th>
                    <th>Status</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
                <tr>
                    <td class="name">
                        {{ r.partnerName }}
                        @if (r.partnerCode) { <span class="mono">{{ r.partnerCode }}</span> }
                    </td>
                    <td>{{ r.fullName || '—' }}</td>
                    <td class="mono">{{ r.panNumber || '—' }}</td>
                    <td class="mono">{{ r.aadhaarNumber || '—' }}</td>
                    <td class="mono">{{ r.gstNumber || '—' }}</td>
                    <td>{{ r.bankName || '—' }}</td>
                    <td><p-tag [value]="r.kycStatus" [severity]="statusSeverity(r.kycStatus)" /></td>
                    <td class="ta-r">
                        <p-button label="Review" icon="pi pi-eye" [outlined]="true" size="small"
                            (onClick)="openReview(r)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="8">
                        <div class="state"><i class="pi pi-inbox"></i>
                            <p>No KYC submissions for this filter.</p>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
</div>

<!-- ===================== REVIEW DIALOG ===================== -->
<p-dialog [visible]="showReview()" (visibleChange)="showReview.set($event)" [modal]="true" [draggable]="true"
    [resizable]="false" [showHeader]="false" [dismissableMask]="true" (onHide)="closeReview()"
    styleClass="kyc-dialog" [style]="{ width: '780px' }" [breakpoints]="{ '820px': '96vw' }">

    @if (reviewLoading()) {
    <div class="kyc-loading"><i class="pi pi-spin pi-spinner"></i><span>Loading review…</span></div>
    } @else if (reviewing(); as k) {

    <!-- ===== Hero header ===== -->
    <div class="kyc-hero">
        <button type="button" class="kyc-x" (click)="closeReview()" aria-label="Close">
            <i class="pi pi-times"></i>
        </button>
        <div class="kyc-avatar">{{ (k.partnerName || 'P').charAt(0) | uppercase }}</div>
        <div class="kyc-id">
            <h3 class="kyc-name">{{ k.partnerName }}</h3>
            <div class="kyc-sub">
                @if (k.partnerCode) { <span class="chip mono">{{ k.partnerCode }}</span> }
                <span class="kyc-applicant"><i class="pi pi-user"></i> {{ k.fullName || '—' }}</span>
            </div>
        </div>
        <span class="kyc-status" [class]="statusClass(k.kycStatus)">
            <i class="pi"
               [class.pi-check-circle]="k.kycStatus === 'Approved'"
               [class.pi-times-circle]="k.kycStatus === 'Rejected'"
               [class.pi-clock]="k.kycStatus === 'Pending'"></i>
            {{ k.kycStatus }}
        </span>
    </div>

    <!-- ===== Scrollable body ===== -->
    <div class="kyc-body">
        @if (k.kycStatus === 'Rejected' && k.rejectionReason) {
        <div class="kyc-note danger"><i class="pi pi-times-circle"></i><span>{{ k.rejectionReason }}</span></div>
        }

        <!-- Applicant details -->
        <section class="kyc-section">
            <header class="kyc-sec-head"><i class="pi pi-building"></i> Company Details</header>
            <div class="kyc-grid">
                <div class="cell"><span>Company Name</span><strong>{{ k.fullName || '—' }}</strong></div>
                <div class="cell"><span>Company PAN</span><strong class="mono">{{ k.panNumber || '—' }}</strong></div>
                <div class="cell"><span>Registration No.</span><strong class="mono">{{ k.aadhaarNumber || '—' }}</strong></div>
                <div class="cell"><span>GST</span><strong class="mono">{{ k.gstNumber || '—' }}</strong></div>
                <div class="cell"><span>Company Email</span><strong>{{ k.email || '—' }}</strong></div>
                <div class="cell"><span>Mobile</span><strong>{{ k.mobileNumber || '—' }}</strong></div>
                <div class="cell span-all"><span>Address</span><strong>{{ k.addressLine1 }}{{ k.addressLine2 ? ', ' + k.addressLine2 : '' }}, {{ k.city }}, {{ k.state }} {{ k.pincode }}</strong></div>
            </div>
        </section>

        <!-- Bank details -->
        <section class="kyc-section">
            <header class="kyc-sec-head"><i class="pi pi-wallet"></i> Bank Details</header>
            <div class="kyc-grid">
                <div class="cell"><span>Bank</span><strong>{{ k.bankName || '—' }}</strong></div>
                <div class="cell"><span>Account Holder</span><strong>{{ k.accountHolderName || '—' }}</strong></div>
                <div class="cell"><span>Account No.</span><strong class="mono">{{ k.accountNumber || '—' }}</strong></div>
                <div class="cell"><span>IFSC</span><strong class="mono">{{ k.ifscCode || '—' }}</strong></div>
            </div>
        </section>

        <!-- Documents -->
        <section class="kyc-section">
            <header class="kyc-sec-head"><i class="pi pi-folder-open"></i> Documents</header>
            <div class="kyc-docs">
                @if (k.panFilePath) { <button type="button" class="doc" (click)="openDoc(k.panFilePath, 'PAN')"><i class="pi pi-file"></i><span>PAN</span><i class="pi pi-eye ext"></i></button> }
                @if (k.aadhaarFilePath) { <button type="button" class="doc" (click)="openDoc(k.aadhaarFilePath, 'Registration')"><i class="pi pi-file"></i><span>Registration</span><i class="pi pi-eye ext"></i></button> }
                @if (k.gstFilePath) { <button type="button" class="doc" (click)="openDoc(k.gstFilePath, 'GST')"><i class="pi pi-file"></i><span>GST</span><i class="pi pi-eye ext"></i></button> }
                @if (k.cancelledChequePath) { <button type="button" class="doc" (click)="openDoc(k.cancelledChequePath, 'Cancelled Cheque')"><i class="pi pi-file"></i><span>Cancelled Cheque</span><i class="pi pi-eye ext"></i></button> }
                @if (!k.panFilePath && !k.aadhaarFilePath && !k.gstFilePath && !k.cancelledChequePath) {
                <span class="muted"><i class="pi pi-inbox"></i> No documents uploaded.</span>
                }
            </div>
        </section>

        <!-- Verification -->
        <section class="kyc-section">
            <header class="kyc-sec-head"><i class="pi pi-verified"></i> Verification</header>
            <div class="kyc-checks">
                <label class="check" [class.on]="vPan()"><p-checkbox [binary]="true" [ngModel]="vPan()" (ngModelChange)="vPan.set($event)" /><span>PAN verified</span></label>
                <label class="check" [class.on]="vAadhaar()"><p-checkbox [binary]="true" [ngModel]="vAadhaar()" (ngModelChange)="vAadhaar.set($event)" /><span>Registration verified</span></label>
                <label class="check" [class.on]="vGst()"><p-checkbox [binary]="true" [ngModel]="vGst()" (ngModelChange)="vGst.set($event)" /><span>GST verified</span></label>
                <label class="check" [class.on]="vBank()"><p-checkbox [binary]="true" [ngModel]="vBank()" (ngModelChange)="vBank.set($event)" /><span>Bank verified</span></label>
            </div>
        </section>

        <!-- Message channels -->
        @if (channels(); as c) {
        <section class="kyc-section">
            <header class="kyc-sec-head">
                <i class="pi pi-send"></i> Message Channels
                <span class="muted">OTP / password delivery</span>
            </header>
            <div class="kyc-checks">
                <label class="check" [class.on]="c.isWhatsApp"><p-checkbox [binary]="true" [ngModel]="c.isWhatsApp" (ngModelChange)="toggleChannel('isWhatsApp')" /><span><i class="pi pi-whatsapp"></i> WhatsApp</span></label>
                <label class="check" [class.on]="c.isSMS"><p-checkbox [binary]="true" [ngModel]="c.isSMS" (ngModelChange)="toggleChannel('isSMS')" /><span><i class="pi pi-comment"></i> SMS</span></label>
                <label class="check" [class.on]="c.isEmail"><p-checkbox [binary]="true" [ngModel]="c.isEmail" (ngModelChange)="toggleChannel('isEmail')" /><span><i class="pi pi-envelope"></i> Email</span></label>
                <p-button [text]="true" size="small" (onClick)="saveChannels()" [disabled]="savingChannels()"
                    label="Save channels" [icon]="savingChannels() ? 'pi pi-spin pi-spinner' : 'pi pi-save'" />
            </div>
        </section>
        }

        <!-- Reject reason -->
        @if (showReject()) {
        <section class="kyc-section">
            <div class="field">
                <label>Rejection reason <span class="req">*</span></label>
                <textarea pTextarea rows="2" [ngModel]="rejectReason()" (ngModelChange)="rejectReason.set($event)"
                    placeholder="e.g. Aadhaar image blurred; re-upload."></textarea>
            </div>
        </section>
        }
    </div>

    <!-- ===== Sticky footer ===== -->
    <div class="kyc-foot">
        <p-button label="Close" [text]="true" severity="secondary" (onClick)="closeReview()" />
        <div class="kyc-foot-actions">
            @if (showReject()) {
            <p-button label="Cancel" [outlined]="true" severity="secondary" (onClick)="showReject.set(false)" />
            <p-button severity="danger" (onClick)="reject()" [disabled]="acting()"
                [label]="acting() ? 'Rejecting…' : 'Confirm Reject'"
                [icon]="acting() ? 'pi pi-spin pi-spinner' : 'pi pi-times'" />
            } @else {
            <p-button label="Reject" icon="pi pi-times" [outlined]="true" severity="danger"
                (onClick)="showReject.set(true)" />
            <p-button (onClick)="approve()" [disabled]="acting()"
                [label]="acting() ? 'Approving…' : 'Approve KYC'"
                [icon]="acting() ? 'pi pi-spin pi-spinner' : 'pi pi-check'" />
            }
        </div>
    </div>
    }
</p-dialog>
